import clsx from 'clsx';
import { Marketplaces } from 'common/constants/enums';
import { UseFormRegister } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  BinIcon,
  CopyIcon,
  EditIcon,
  EyeIcon,
} from '../../../../assets/images/icons';
import { Button } from '../../../../common/ui/button';
import { Tooltip } from '../../../../common/ui/tooltip';
import { utilCopyToClipboard } from '../../../../common/utils';
import {
  ITagCreationForm,
  MARKETPLACE_BASE_URLS,
  MARKETPLACE_LOGOS,
  resolveLink,
} from './temporary-links-form.constants';
import styles from './temporary-links-form.module.scss';

interface LinkEditRowProps {
  index: number;
  fieldId: string;
  marketplace: Marketplaces;
  isAdvanced: boolean;
  isDeleteDisabled: boolean;
  tagError?: string;
  itemIDError?: string;
  register: UseFormRegister<ITagCreationForm>;
  getValues: (name: `tags.${number}.itemID` | `tags.${number}.tag`) => string;
  onShowValues: () => void;
  onToggleAdvanced: () => void;
  onDelete: () => void;
}

export const LinkEditRow = ({
  getValues,
  index,
  isAdvanced,
  isDeleteDisabled,
  itemIDError,
  marketplace,
  onDelete,
  onShowValues,
  onToggleAdvanced,
  register,
  tagError,
}: LinkEditRowProps) => {
  const copyLink = () => {
    const itemID = getValues(`tags.${index}.itemID`);
    const tag = getValues(`tags.${index}.tag`);

    if (isAdvanced) {
      if (tag) {
        utilCopyToClipboard(tag);
      } else {
        toast.error('Type full link');
      }
      return;
    }
    if (itemID && tag) {
      utilCopyToClipboard(resolveLink(itemID, tag, marketplace, false));
    } else if (!itemID && tag) {
      toast.error('Type itemID');
    } else if (!tag && itemID) {
      toast.error('Type tags');
    } else {
      toast.error('Type product itemID and tags');
    }
  };

  return (
    <div className={styles.form_item}>
      <img
        alt={`${marketplace}_icon`}
        className={styles.link_logo}
        src={MARKETPLACE_LOGOS[marketplace]}
      />
      {isAdvanced ? (
        <>
          <div className={styles.input_wrapper}>
            <input
              autoComplete="off"
              className={clsx(styles.input, styles.itemID, itemIDError && styles.input__error)}
              {...register(`tags.${index}.itemID`)}
              placeholder="itemID..."
            />
            {itemIDError && <p className={styles.error_message}>{itemIDError}</p>}
          </div>
          <div className={styles.input_wrapper}>
            <input
              autoComplete="off"
              className={clsx(styles.input, styles.full_link, tagError && styles.input__error)}
              {...register(`tags.${index}.tag`)}
              placeholder="full link..."
            />
            {tagError && <p className={styles.error_message}>{tagError}</p>}
          </div>
        </>
      ) : (
        <>
          {MARKETPLACE_BASE_URLS[marketplace]}
          <div className={styles.input_wrapper}>
            <input
              autoComplete="off"
              className={clsx(styles.input, styles.itemID, itemIDError && styles.input__error)}
              {...register(`tags.${index}.itemID`)}
              placeholder="itemID..."
            />
            {itemIDError && <p className={styles.error_message}>{itemIDError}</p>}
          </div>
          {' ? '}
          <div className={styles.input_wrapper}>
            <input
              autoComplete="off"
              className={clsx(styles.input, styles.tags, tagError && styles.input__error)}
              {...register(`tags.${index}.tag`)}
              placeholder="tags..."
            />
            {tagError && <p className={styles.error_message}>{tagError}</p>}
          </div>
        </>
      )}
      <div className={styles.actions}>
        <Tooltip title="show values">
          <Button
            iconStart={<EyeIcon />}
            onClick={onShowValues}
            outlined
            size="small"
            variant="secondary"
          />
        </Tooltip>
        <Tooltip title="copy link">
          <Button
            iconStart={<CopyIcon />}
            onClick={copyLink}
            outlined
            size="small"
            variant="secondary"
          />
        </Tooltip>
        <Tooltip title={isAdvanced ? 'standard mode' : 'advanced mode'}>
          <Button
            iconStart={<EditIcon />}
            onClick={onToggleAdvanced}
            outlined
            size="small"
            variant={isAdvanced ? 'primary' : 'secondary'}
          />
        </Tooltip>
        <Tooltip title="delete item">
          <Button
            disabled={isDeleteDisabled}
            iconStart={<BinIcon />}
            onClick={onDelete}
            outlined
            size="small"
            variant="danger"
          />
        </Tooltip>
      </div>
    </div>
  );
};
